// dataService.ts - Frontend data processing and calculations
import { ScannerData } from "../types/ScannerData";
import { getApiUrl } from "../config/api";
import { getAuthToken, forceSessionExpired } from "../utils/authUtils";
import { realTimePriceService } from "./realTimePriceService";
import {
    SymbolSeparationService,
    SeparatedData,
} from "./symbolSeparationService";
import {
    centralizedDataService,
    DataPacket,
    DEFAULT_SCAN_CONFIG,
} from "./centralizedDataService";
import { getCBOEFieldValue, recordHasGammaOrOI } from "../utils/dataModeUtils";
import {
    readStableUnderlyingsSessionFirst,
    readStablePrimarySessionFirst,
} from "../utils/stableTabStorage";
import { coerceStrikeParamsForUnderlyings } from "../utils/stableStrikeParams";
import { withSnapshotDate } from "./stableAccumulatedRequest";
import {
    extractStableProgress,
    setLastStableProgress,
} from "./stableFetchProgress";

// Helper: parse number from record field; CBOE may send "-" or string numbers
function safeNum(value: unknown): number {
    if (value === null || value === undefined || value === "" || value === "-")
        return 0;
    const n = parseFloat(String(value).replace(/,/g, ""));
    return Number.isNaN(n) ? 0 : n;
}

/** Calendar days to expiry for display/BS; never NaN (Invalid Date makes Math.max(..., NaN) === NaN). */
function daysLeftFromExpiryField(
    expiryDate: unknown,
    fallback = 0.0001,
): number {
    if (expiryDate == null || expiryDate === "") return fallback;
    const today = new Date();
    const expiry = new Date(expiryDate as string | number | Date);
    const expMs = expiry.getTime();
    const todayMs = today.getTime();
    if (!Number.isFinite(expMs) || !Number.isFinite(todayMs)) return fallback;
    const diffDays = Math.ceil((expMs - todayMs) / (1000 * 60 * 60 * 24));
    if (!Number.isFinite(diffDays)) return fallback;
    return Math.max(0.0001, diffDays);
}

function needsDaysLeftFromExpiry(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === "number") return !Number.isFinite(value);
    const n = parseFloat(String(value).replace(/,/g, ""));
    return !Number.isFinite(n);
}

/** Greek handling for `calculateHeavyFields` — never keyed off row `Source` (product rule). */
export type CalculateHeavyFieldsOptions = {
    /**
     * Set when processing `GET /api/accumulated-data?source=stable` (Theta chain snapshot).
     * Keeps feed Greeks and only BS-fills missing fields. Legacy RTL (`rtl_legacy`) omits this so
     * Black–Scholes / volume-based paths stay unchanged vs stable.
     */
    stableThetaChain?: boolean;
};

/** Fire-and-forget: record views for underlyings in one request (avoids 50 POSTs and 502s). */
function trackUnderlyingViews(underlyings: string[]): void {
    const uniq = Array.from(
        new Set(
            underlyings
                .map((u) =>
                    String(u || "")
                        .trim()
                        .toUpperCase(),
                )
                .filter(Boolean),
        ),
    ).slice(0, 50);
    if (uniq.length === 0) return;
    fetch(getApiUrl("/api/track-view"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbols: uniq }),
        keepalive: true,
    }).catch(() => {
        /* ignore */
    });
}

/** Build strike query params from localStorage (mode: count | all | custom). */
function getExpiryParams(): { expiry_range?: string; expiry?: string } {
    const expiryRange = (
        localStorage.getItem("mmec_expiry_range") || "weekly"
    ).trim();
    const exactDate = (localStorage.getItem("mmec_expiry_date") || "").trim();
    if (expiryRange === "specific-date" && exactDate) {
        return { expiry_range: "specific-date", expiry: exactDate };
    }
    // Custom multi-select: pass the comma-separated dates as expiry param.
    // Backend treats multiple dates as an explicit expiration list.
    if (expiryRange === "custom" && exactDate) {
        return { expiry_range: "custom", expiry: exactDate };
    }
    return expiryRange ? { expiry_range: expiryRange } : {};
}

/**
 * Extra query segment for stable `accumulated-data` URLs (strike/expiry from localStorage prefs).
 * Append as `&${getStableAccumulatedDataExtraQueryParams()}` after underlyings; returns "" if nothing to add.
 * Uses coerceStrikeParamsForUnderlyings from stableStrikeParams to auto-fallback index options.
 */
export function getStableAccumulatedDataExtraQueryParams(): string {
    if (typeof localStorage === "undefined") return "";
    try {
        const strikeParams = coerceStrikeParamsForUnderlyings(
            readStableUnderlyingsSessionFirst(),
        );
        const expiryParams = getExpiryParams();
        const parts: string[] = [];
        if (expiryParams.expiry_range) {
            parts.push(
                `expiry_range=${encodeURIComponent(expiryParams.expiry_range)}`,
            );
        }
        if (expiryParams.expiry) {
            parts.push(`expiry=${encodeURIComponent(expiryParams.expiry)}`);
        }
        if (
            strikeParams.strike_min != null &&
            strikeParams.strike_max != null
        ) {
            parts.push(
                `strike_min=${encodeURIComponent(String(strikeParams.strike_min))}`,
            );
            parts.push(
                `strike_max=${encodeURIComponent(String(strikeParams.strike_max))}`,
            );
        } else if (strikeParams.strike_range) {
            parts.push(
                `strike_range=${encodeURIComponent(strikeParams.strike_range)}`,
            );
        }
        return parts.join("&");
    } catch {
        return "";
    }
}

// Heavy calculations — Legacy RTL keeps its own path (prefill + BS when IV allows).
// Stable Theta chain (`source=stable`): preserve snapshot Greeks from backend; BS only fills gaps (see stableThetaChain).
// Canonical: Delta ( ask - bid ) = Ask Volume - Bid Volume; Delta accumelated = Ask Vol accum - Bid Vol accum;
// Delta Premium = Delta * Last * 100; Delta Accumulated Premium = Delta accumelated * Last * 100.
export const calculateHeavyFields = (
    record: ScannerData,
    options?: CalculateHeavyFieldsOptions,
): ScannerData => {
    try {
        if (
            record == null ||
            typeof record !== "object" ||
            Array.isArray(record)
        ) {
            return {
                Underlying: "—",
                Strike: 0,
                "Option Type": "C",
            } as ScannerData;
        }
        if (options?.stableThetaChain === true) {
            const out = { ...record };
            if (needsDaysLeftFromExpiry(out["Days left"])) {
                const expiryDate = record["Expiry Date"];
                out["Days left"] = daysLeftFromExpiryField(expiryDate);
            }
            if (out["MMEC Gap"] == null) {
                const uPrice = safeNum(record["Underlying Price"]);
                const lPrice = safeNum(record["Last"]);
                out["MMEC Gap"] =
                    uPrice > 0 && lPrice > 0
                        ? ((uPrice - lPrice) / uPrice) * 100
                        : 0;
            }
            const { haltz, band, mal, zahn } =
                calculateTechnicalIndicators(record);
            out["Haltz (Volume Concentration)"] = haltz;
            out["Band (IV Indicator)"] = band;
            out["Mal (Gamma Indicator)"] = mal;
            out["Zahn (Count Indicator)"] = zahn;
            // Stable / snapshot: Volume and Total Volume are the same (session contracts); notional fields if backend omitted
            const vol = safeNum(out["Volume"]);
            const lastPx = safeNum(out["Last"]);
            const bidPx = safeNum(out["Bid"]);
            const askPx = safeNum(out["Ask"]);
            const effLast =
                lastPx > 0
                    ? lastPx
                    : bidPx > 0 && askPx > 0
                      ? (bidPx + askPx) / 2
                      : 0;
            const oi = safeNum(out["Open Interest"]);
            out["Total Volume"] = vol;
            if (
                out["Total Premium"] === undefined ||
                out["Total Premium"] === null
            ) {
                out["Total Premium"] = effLast > 0 ? vol * effLast * 100 : 0;
            }
            if (
                out["Overall Premium"] === undefined ||
                out["Overall Premium"] === null
            ) {
                out["Overall Premium"] = effLast > 0 ? vol * effLast * 100 : 0;
            }
            if (
                out["Open Interest Dollar"] === undefined ||
                out["Open Interest Dollar"] === null
            ) {
                out["Open Interest Dollar"] =
                    effLast > 0 ? oi * effLast * 100 : 0;
            }
            if (
                out["Fresh Volume %"] === undefined ||
                out["Fresh Volume %"] === null
            ) {
                out["Fresh Volume %"] = oi > 0 ? (vol / oi) * 100 : 0;
            }
            if (out["Vol/OI"] === undefined || out["Vol/OI"] === null) {
                out["Vol/OI"] = oi > 0 ? vol / oi : 0;
            }
            const optionTypePreserve =
                out["Option Type"] === "P" ||
                String(out["Option Type"]).toUpperCase() === "PUT"
                    ? "P"
                    : "C";
            mergeBlackScholesGreeksIfNeeded(out, record, optionTypePreserve);
            return out;
        }
        const updatedRecord = { ...record };

        // Extract basic values (safe for CBOE "-" and string numbers)
        const underlyingPrice = safeNum(record["Underlying Price"]);
        const bid = safeNum(record["Bid"]);
        const ask = safeNum(record["Ask"]);
        const lastPrice = safeNum(record["Last"]);
        const midPrice = safeNum(record["Mid"]) || (bid + ask) / 2 || lastPrice;
        // Use Last for premium (Delta * Last * 100); fallback to Mid when Last is 0 (all data modes)
        const effectivePrice = lastPrice > 0 ? lastPrice : midPrice;
        const strike = safeNum(record["Strike"]);
        const optionType =
            record["Option Type"] === "P" ||
            String(record["Option Type"]).toUpperCase() === "PUT"
                ? "P"
                : "C";
        const impliedVol = safeNum(record["Implied Volatility"]);

        // All modes: use Volume for delta and premium (Delta = Ask Volume - Bid Volume, Premium = Delta * Last * 100)
        const bidCount = safeNum(record["Bid Count"]);
        const askCount = safeNum(record["Ask Count"]);
        const bidVolume = safeNum(record["Bid Volume"]);
        const askVolume = safeNum(record["Ask Volume"]);
        const hasVolume =
            bidVolume !== 0 ||
            askVolume !== 0 ||
            (record["Bid Volume"] != null && record["Ask Volume"] != null);
        const effectiveBid = hasVolume
            ? bidVolume
            : bidCount > 0
              ? bidCount
              : bidVolume;
        const effectiveAsk = hasVolume
            ? askVolume
            : askCount > 0
              ? askCount
              : askVolume;

        const bidCountAccumulated = safeNum(record["Bid Count accumelated"]);
        const askCountAccumulated = safeNum(record["Ask Count accumelated"]);
        const bidVolumeAccumulated = safeNum(record["Bid Volume accumelated"]);
        const askVolumeAccumulated = safeNum(record["Ask Volume accumelated"]);

        // Accumulated delta and premium use VOLUME accumulated (delta ACC * last * 100). Prefer volume when present (RTL).
        const hasVolumeAccumulated =
            bidVolumeAccumulated !== 0 ||
            askVolumeAccumulated !== 0 ||
            (record["Bid Volume accumelated"] != null &&
                record["Ask Volume accumelated"] != null);
        const effectiveBidAcc = hasVolumeAccumulated
            ? bidVolumeAccumulated
            : bidCountAccumulated > 0
              ? bidCountAccumulated
              : bidVolumeAccumulated;
        const effectiveAskAcc = hasVolumeAccumulated
            ? askVolumeAccumulated
            : askCountAccumulated > 0
              ? askCountAccumulated
              : askVolumeAccumulated;

        // Calculate MMEC Gap using TradeStation formula: (Open - Close[1]) / Open * 100
        // For options, we'll use: (Current Price - Previous Close) / Current Price * 100
        if (underlyingPrice > 0 && lastPrice > 0) {
            const gapValue = underlyingPrice - lastPrice;
            const gapPercent = (gapValue / underlyingPrice) * 100;
            updatedRecord["MMEC Gap"] = gapPercent;
        } else {
            updatedRecord["MMEC Gap"] = 0;
        }

        // Calculate Delta (ask - bid) for volume/count difference
        const delta = effectiveAsk - effectiveBid;
        const deltaAccumulated = effectiveAskAcc - effectiveBidAcc;

        // Calculate Days Left (heavy calculation); guard Invalid Date so UI never shows NaN
        const expiryDate = record["Expiry Date"];
        updatedRecord["Days left"] = daysLeftFromExpiryField(expiryDate);

        // Calculate Premium Fields using effective counts/volumes
        const bidVolumePremium = effectiveBid * effectivePrice * 100;
        const askVolumePremium = effectiveAsk * effectivePrice * 100;
        const deltaPremium = delta * effectivePrice * 100;
        const bidAccumulatedPremium = effectiveBidAcc * effectivePrice * 100;
        const askAccumulatedPremium = effectiveAskAcc * effectivePrice * 100;
        const deltaAccumulatedPremium = deltaAccumulated * effectivePrice * 100;

        // Only set fields if not already set by backend (use proper null/undefined checks, not truthy)
        // 0 is a valid value and should NOT be overwritten
        if (
            updatedRecord["Bid Volume Premium"] === undefined ||
            updatedRecord["Bid Volume Premium"] === null
        ) {
            updatedRecord["Bid Volume Premium"] = bidVolumePremium;
        }
        if (
            updatedRecord["Ask Volume Premium"] === undefined ||
            updatedRecord["Ask Volume Premium"] === null
        ) {
            updatedRecord["Ask Volume Premium"] = askVolumePremium;
        }
        if (
            updatedRecord["Delta Premium"] === undefined ||
            updatedRecord["Delta Premium"] === null
        ) {
            updatedRecord["Delta Premium"] = deltaPremium;
        }
        if (
            updatedRecord["Bid Accumulated Premium"] === undefined ||
            updatedRecord["Bid Accumulated Premium"] === null
        ) {
            updatedRecord["Bid Accumulated Premium"] = bidAccumulatedPremium;
        }
        if (
            updatedRecord["Ask Accumulated Premium"] === undefined ||
            updatedRecord["Ask Accumulated Premium"] === null
        ) {
            updatedRecord["Ask Accumulated Premium"] = askAccumulatedPremium;
        }
        if (
            updatedRecord["Delta Accumulated Premium"] === undefined ||
            updatedRecord["Delta Accumulated Premium"] === null
        ) {
            updatedRecord["Delta Accumulated Premium"] =
                deltaAccumulatedPremium;
        }

        // Delta ( ask - bid ) is the volume difference - don't overwrite if already set
        if (
            updatedRecord["Delta ( ask - bid )"] === undefined ||
            updatedRecord["Delta ( ask - bid )"] === null
        ) {
            updatedRecord["Delta ( ask - bid )"] = delta;
        }
        // Delta accumelated is the accumulated volume difference - don't overwrite if already set
        if (
            updatedRecord["Delta accumelated"] === undefined ||
            updatedRecord["Delta accumelated"] === null
        ) {
            updatedRecord["Delta accumelated"] = deltaAccumulated;
        }

        // Black-Scholes Greeks when inputs allow (standard fallback so rows are not left at zero)
        if (
            underlyingPrice > 0 &&
            strike > 0 &&
            impliedVol > 0 &&
            updatedRecord["Days left"] > 0
        ) {
            const greeks = calculateBlackScholesGreeks(
                underlyingPrice,
                strike,
                updatedRecord["Days left"] / 365, // Convert to years
                0.05, // Risk-free rate (5% approximation)
                impliedVol / 100, // Convert IV to decimal
                optionType === "C" ? "call" : "put",
            );

            updatedRecord["Delta"] = greeks.delta; // Greek Delta (option sensitivity)
            updatedRecord["Gamma"] = greeks.gamma;
            updatedRecord["Theta"] = greeks.theta;
            updatedRecord["Vega"] = greeks.vega;
            updatedRecord["Rho"] = greeks.rho;
            updatedRecord["Theoretical Value"] = greeks.theoreticalValue;

            // Calculate Lambda: Delta * Underlying Price / Market Value
            const marketValue =
                lastPrice > 0
                    ? lastPrice
                    : (parseFloat(String(record["Bid"] || 0)) +
                          parseFloat(String(record["Ask"] || 0))) /
                      2;
            const lambda =
                marketValue > 0
                    ? (greeks.delta * underlyingPrice) / marketValue
                    : 0;
            updatedRecord["Lambda"] = lambda;
        } else {
            // Do not wipe feed Greeks when Black–Scholes inputs are incomplete (e.g. IV=0 from snapshot but Theta gamma is valid).
            if (needsGreekFallback(updatedRecord["Delta"]))
                updatedRecord["Delta"] = 0;
            if (needsGreekFallback(updatedRecord["Gamma"]))
                updatedRecord["Gamma"] = 0;
            if (needsGreekFallback(updatedRecord["Theta"]))
                updatedRecord["Theta"] = 0;
            if (needsGreekFallback(updatedRecord["Vega"]))
                updatedRecord["Vega"] = 0;
            if (needsGreekFallback(updatedRecord["Rho"]))
                updatedRecord["Rho"] = 0;
            if (needsGreekFallback(updatedRecord["Lambda"]))
                updatedRecord["Lambda"] = 0;
            if (needsGreekFallback(updatedRecord["Theoretical Value"]))
                updatedRecord["Theoretical Value"] = 0;
        }

        // Calculate Zahn, Halt, Band, Mal indicators
        const { haltz, band, mal, zahn } = calculateTechnicalIndicators(record);
        updatedRecord["Haltz (Volume Concentration)"] = haltz;
        updatedRecord["Band (IV Indicator)"] = band;
        updatedRecord["Mal (Gamma Indicator)"] = mal;
        updatedRecord["Zahn (Count Indicator)"] = zahn;

        // Match backend: Volume === Total Volume; notional fields (Legacy/New RTL if backend skipped)
        const canonVolRtl = safeNum(updatedRecord["Volume"]);
        updatedRecord["Total Volume"] = canonVolRtl;
        const elRtl =
            safeNum(updatedRecord["Last"]) > 0
                ? safeNum(updatedRecord["Last"])
                : safeNum(updatedRecord["Bid"]) > 0 &&
                    safeNum(updatedRecord["Ask"]) > 0
                  ? (safeNum(updatedRecord["Bid"]) +
                        safeNum(updatedRecord["Ask"])) /
                    2
                  : 0;
        if (
            updatedRecord["Total Premium"] === undefined ||
            updatedRecord["Total Premium"] === null
        ) {
            updatedRecord["Total Premium"] =
                elRtl > 0 ? canonVolRtl * elRtl * 100 : 0;
        }
        if (
            updatedRecord["Overall Premium"] === undefined ||
            updatedRecord["Overall Premium"] === null
        ) {
            updatedRecord["Overall Premium"] =
                elRtl > 0 ? canonVolRtl * elRtl * 100 : 0;
        }
        const oiRtl = safeNum(updatedRecord["Open Interest"]);
        if (
            updatedRecord["Open Interest Dollar"] === undefined ||
            updatedRecord["Open Interest Dollar"] === null
        ) {
            updatedRecord["Open Interest Dollar"] =
                elRtl > 0 ? oiRtl * elRtl * 100 : 0;
        }
        if (
            updatedRecord["Fresh Volume %"] === undefined ||
            updatedRecord["Fresh Volume %"] === null
        ) {
            updatedRecord["Fresh Volume %"] =
                oiRtl > 0 ? (canonVolRtl / oiRtl) * 100 : 0;
        }
        if (
            updatedRecord["Vol/OI"] === undefined ||
            updatedRecord["Vol/OI"] === null
        ) {
            updatedRecord["Vol/OI"] = oiRtl > 0 ? canonVolRtl / oiRtl : 0;
        }

        return updatedRecord;
    } catch (error) {
        console.error("Error calculating heavy fields:", error);
        if (
            record != null &&
            typeof record === "object" &&
            !Array.isArray(record)
        ) {
            return record as ScannerData;
        }
        return {
            Underlying: "—",
            Strike: 0,
            "Option Type": "C",
        } as ScannerData;
    }
};

function applyHeavyFieldsSafe(
    record: ScannerData,
    options?: CalculateHeavyFieldsOptions,
): ScannerData {
    try {
        return calculateHeavyFields(record, options);
    } catch {
        return record;
    }
}

/**
 * Legacy RTL (Excel) rows often omit Underlying Price on some lines or leave IV empty while
 * Band (IV Indicator) is filled. Fill gaps so calculateHeavyFields can run Black–Scholes
 * and charts (Detailed Options, gamma, etc.) get non-zero Greeks when mathematically possible.
 */
function prefillLegacyRtlForHeavyFields(rows: ScannerData[]): ScannerData[] {
    if (!rows.length) return rows;
    const byUnd = new Map<string, number>();
    for (const r of rows) {
        const u = String(r.Underlying || (r as any).underlying_symbol || "")
            .trim()
            .toUpperCase();
        if (!u) continue;
        const up = safeNum(r["Underlying Price"]);
        if (up > 0) byUnd.set(u, Math.max(byUnd.get(u) || 0, up));
    }
    return rows.map((r) => {
        const out = { ...r } as ScannerData;
        const u = String(out.Underlying || (out as any).underlying_symbol || "")
            .trim()
            .toUpperCase();
        const fillUp = u ? byUnd.get(u) : undefined;
        if (
            fillUp != null &&
            fillUp > 0 &&
            safeNum(out["Underlying Price"]) <= 0
        ) {
            out["Underlying Price"] = fillUp;
        }
        let iv = safeNum(out["Implied Volatility"]);
        if (iv <= 0) {
            const band = safeNum(out["Band (IV Indicator)"]);
            if (band > 0) {
                out["Implied Volatility"] = band;
                iv = band;
            }
        }
        if (iv > 0 && iv < 1) {
            out["Implied Volatility"] = iv * 100;
        }
        return out;
    });
}

/** After BS enrichment: row can drive gamma/OI charts (not only raw CBOE keys). */
function legacyRowHasChartSignal(r: ScannerData): boolean {
    if (recordHasGammaOrOI(r)) return true;
    const g = getCBOEFieldValue(r, "Gamma");
    const oi = getCBOEFieldValue(r, "Open Interest");
    if (g !== 0 || oi !== 0) return true;
    return (
        safeNum(r["Implied Volatility"]) > 0 &&
        safeNum(r.Strike) > 0 &&
        safeNum(r["Underlying Price"]) > 0
    );
}

// Black-Scholes Greeks calculation
function calculateBlackScholesGreeks(
    S: number,
    K: number,
    T: number,
    r: number,
    sigma: number,
    optionType: "call" | "put",
) {
    const d1 =
        (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) /
        (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Standard normal CDF approximation
    const N = (x: number) => 0.5 * (1 + erf(x / Math.sqrt(2)));
    const n = (x: number) => Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);

    let delta, gamma, theta, vega, rho, theoreticalValue;

    if (optionType === "call") {
        delta = N(d1);
        theoreticalValue = S * N(d1) - K * Math.exp(-r * T) * N(d2);
        theta =
            -(S * n(d1) * sigma) / (2 * Math.sqrt(T)) -
            r * K * Math.exp(-r * T) * N(d2);
        rho = K * T * Math.exp(-r * T) * N(d2);
    } else {
        delta = N(d1) - 1;
        theoreticalValue = K * Math.exp(-r * T) * N(-d2) - S * N(-d1);
        theta =
            -(S * n(d1) * sigma) / (2 * Math.sqrt(T)) +
            r * K * Math.exp(-r * T) * N(-d2);
        rho = -K * T * Math.exp(-r * T) * N(-d2);
    }

    gamma = n(d1) / (S * sigma * Math.sqrt(T));
    vega = S * n(d1) * Math.sqrt(T);

    return { delta, gamma, theta, vega, rho, theoreticalValue };
}

const GREEK_FALLBACK_MIN_ABS = 1e-12;

/** Missing, non-numeric, or effectively zero → replace with Black–Scholes when S/K/IV/T allow. */
function needsGreekFallback(value: unknown): boolean {
    if (value === undefined || value === null || value === "" || value === "-")
        return true;
    const n =
        typeof value === "number"
            ? value
            : parseFloat(String(value).replace(/,/g, ""));
    if (!Number.isFinite(n)) return true;
    return Math.abs(n) < GREEK_FALLBACK_MIN_ABS;
}

/**
 * Provider rows: keep meaningful feed Greeks; fill missing/zero first-order Greeks via Black–Scholes (standard procedure).
 */
function mergeBlackScholesGreeksIfNeeded(
    out: ScannerData,
    recordForPrices: ScannerData,
    optionType: "C" | "P",
): void {
    const underlyingPrice = safeNum(out["Underlying Price"]);
    const strike = safeNum(out["Strike"]);
    const impliedVol = safeNum(out["Implied Volatility"]);
    if (underlyingPrice <= 0 || strike <= 0 || impliedVol <= 0) return;

    const daysLeft = safeNum(out["Days left"]);
    if (!(daysLeft > 0)) return;

    if (
        !needsGreekFallback(out["Delta"]) &&
        !needsGreekFallback(out["Gamma"]) &&
        !needsGreekFallback(out["Theta"]) &&
        !needsGreekFallback(out["Vega"])
    ) {
        return;
    }

    const greeks = calculateBlackScholesGreeks(
        underlyingPrice,
        strike,
        daysLeft / 365,
        0.05,
        impliedVol / 100,
        optionType === "C" ? "call" : "put",
    );

    if (needsGreekFallback(out["Delta"])) out["Delta"] = greeks.delta;
    if (needsGreekFallback(out["Gamma"])) out["Gamma"] = greeks.gamma;
    if (needsGreekFallback(out["Theta"])) out["Theta"] = greeks.theta;
    if (needsGreekFallback(out["Vega"])) out["Vega"] = greeks.vega;
    if (needsGreekFallback(out["Rho"])) out["Rho"] = greeks.rho;
    if (needsGreekFallback(out["Theoretical Value"]))
        out["Theoretical Value"] = greeks.theoreticalValue;

    const lastPrice = safeNum(recordForPrices["Last"]);
    const bid = safeNum(recordForPrices["Bid"]);
    const ask = safeNum(recordForPrices["Ask"]);
    const marketValue =
        lastPrice > 0 ? lastPrice : bid > 0 && ask > 0 ? (bid + ask) / 2 : 0;
    if (needsGreekFallback(out["Lambda"]) && marketValue > 0) {
        out["Lambda"] = (Number(out["Delta"]) * underlyingPrice) / marketValue;
    }
}

// Error function approximation
function erf(x: number): number {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y =
        1.0 -
        ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
}

// Technical indicators calculation - Percentage-based buy/sell ratios
function calculateTechnicalIndicators(record: ScannerData) {
    const impliedVol = parseFloat(String(record["Implied Volatility"] || 0));
    const bidCount = parseFloat(String(record["Bid Count"] || 0));
    const askCount = parseFloat(String(record["Ask Count"] || 0));
    const underlyingPrice = parseFloat(String(record["Underlying Price"] || 0));
    const strike = parseFloat(String(record["Strike"] || 0));
    const bidVolume = parseFloat(
        String(record["Bid Volume"] || record["Bid Count"] || 0),
    );
    const askVolume = parseFloat(
        String(record["Ask Volume"] || record["Ask Count"] || 0),
    );

    // Haltz (Volume Concentration) - Buy/Sell volume ratio
    // +100% = all buying pressure, -100% = all selling pressure, 0% = neutral
    const totalVolume = bidVolume + askVolume;
    const haltz =
        totalVolume > 0 ? ((askVolume - bidVolume) / totalVolume) * 100 : 0;

    // Band (IV Indicator) - Implied Volatility level
    // High positive = high IV, negative = low IV
    const band =
        impliedVol > 0 ? Math.min(100, Math.max(-100, impliedVol - 30)) : 0;

    // Mal (Gamma Indicator) - Distance from ATM (at-the-money)
    // 0% = ATM, higher = further OTM/ITM
    const moneyness =
        underlyingPrice > 0 && strike > 0 ? strike / underlyingPrice : 1;
    const mal = Math.min(100, Math.max(-100, (moneyness - 1) * 100));

    // Zahn (Count Indicator) - Buy/Sell count ratio
    // +100% = all ask activity (buying), -100% = all bid activity (selling), 0% = neutral
    const totalCount = bidCount + askCount;
    const zahn =
        totalCount > 0 ? ((askCount - bidCount) / totalCount) * 100 : 0;

    return { haltz, band, mal, zahn };
}

// Standardized field names to match backend fixes
export const FIELD_MAPPINGS = {
    // Core fields
    symbol: "Symbol",
    underlying: "Underlying",
    strike: "Strike",
    expiryDate: "Expiry Date",
    optionType: "Option Type",
    side: "Side",

    // Volume fields
    volume: "Volume",
    bidVolume: "Bid Volume",
    askVolume: "Ask Volume",
    bidCount: "Bid Count",
    askCount: "Ask Count",

    // Greeks
    delta: "Delta",
    gamma: "Gamma",
    theta: "Theta",
    vega: "Vega",
    impliedVolatility: "Implied Volatility",

    // Price fields
    last: "Last",
    underlyingPrice: "Underlying Price",
    theoreticalValue: "Theoretical Value",
    mid: "Mid",

    // Calculated fields
    haltz: "Haltz",
    band: "Band",
    zahn: "Zahn",
    properSentiment: "Proper Sentiment",
    properDirection: "Proper Direction",
    underlyingSentiment: "Underlying Sentiment",
    optionStrategy: "Option Strategy",
    moneyness: "Moneyness",

    // Accumulated fields
    deltaAccumulated: "Delta accumelated",
    directionAccumulated: "Direction accumelated",
    bidVolumeAccumulated: "Bid Volume accumelated",
    askVolumeAccumulated: "Ask Volume accumelated",

    // 5-minute fields
    direction5min: "Direction",
    delta5min: "Delta ( ask - bid )",
};

export enum ChartType {
    VOLUME_BY_SYMBOL = "volume_by_symbol",
    VOLUME_BY_STRIKE = "volume_by_strike",
    DELTA_BY_SYMBOL = "delta_by_symbol",
    DELTA_BY_STRIKE = "delta_by_strike",
    IV_BY_SYMBOL = "iv_by_symbol",
    IV_BY_STRIKE = "iv_by_strike",
    SENTIMENT_BY_SYMBOL = "sentiment_by_symbol",
    SENTIMENT_BY_STRIKE = "sentiment_by_strike",
    HALTZ_BY_SYMBOL = "haltz_by_symbol",
    BAND_BY_SYMBOL = "band_by_symbol",
    ZAHN_BY_SYMBOL = "zahn_by_symbol",
    MONEYNESS_DISTRIBUTION = "moneyness_distribution",
    OPTION_TYPE_DISTRIBUTION = "option_type_distribution",
    VOLUME_DELTA_CORRELATION = "volume_delta_correlation",
    IV_VOLUME_CORRELATION = "iv_volume_correlation",
    STRIKE_VOLUME_HEATMAP = "strike_volume_heatmap",
    EXPIRY_VOLUME_HEATMAP = "expiry_volume_heatmap",
    UNDERLYING_SENTIMENT = "underlying_sentiment",
    OPTION_STRATEGY_DISTRIBUTION = "option_strategy_distribution",
    DIRECTION_ANALYSIS = "direction_analysis",
    ACCUMULATED_ANALYSIS = "accumulated_analysis",
    FIVE_MINUTE_ANALYSIS = "five_minute_analysis",
    COMPREHENSIVE_ANALYSIS = "comprehensive_analysis",
}

class DataService {
    private data: ScannerData[] = [];
    private lastFetch: Date | null = null;
    private isLoading = false;
    /** RTL and stable refresh run independently; only block same-source concurrent requests */
    private isLoadingRtl = false;
    private isLoadingStable = false;
    private error: string | null = null;
    private centralizedServiceInitialized = false;
    /** Last X-Data-Source response header: 'rtl-remote' | 'rtl-legacy-excel' | 'stable' | 'theta-stream' | null */
    lastResponseDataSource: string | null = null;
    /** When source is RTL Stream and response is empty, backend may send a hint (e.g. "Stream not connected..."). */
    lastStreamHint: string | null = null;
    /** Total trades currently in the stream buffer (from last /api/theta-stream/recent response). */
    lastStreamBufferSize: number | null = null;
    /** Unique underlyings in last stream response. */
    lastStreamUniqueUnderlyings: number | null = null;
    /** Unused: stream is full-trade only; fetch other data from backend (New RTL / Stable). */
    lastStreamMergeUnderlyings: string[] | null = null;
    /** Level 2 totals from last accumulated-data response (New RTL / Stable): total bid size, ask size, open interest. */
    lastLevel2Totals: {
        total_bid_volume: number;
        total_ask_volume: number;
        total_open_interest?: number;
    } | null = null;
    /** After rtl_legacy hits a transient failure (timeout or connection reset), cooldown before retry (ms). */
    private lastRtlLegacyTimeoutAt: number | null = null;
    /** Long enough to outlast typical Docker/Caddy restarts without hammering the edge. */
    private static readonly RTL_LEGACY_BACKOFF_MS = 60_000;

    // Get standardized field value
    getFieldValue(item: ScannerData, fieldName: string): any {
        const standardField =
            FIELD_MAPPINGS[fieldName as keyof typeof FIELD_MAPPINGS];
        if (
            standardField &&
            item[standardField as keyof ScannerData] !== undefined
        ) {
            return item[standardField as keyof ScannerData];
        }

        // Fallback to direct field access
        return item[fieldName as keyof ScannerData];
    }

    // Get field name for display
    getFieldName(fieldKey: string): string {
        return (
            FIELD_MAPPINGS[fieldKey as keyof typeof FIELD_MAPPINGS] || fieldKey
        );
    }

    async fetchRawData(
        dataMode: "5min" | "accumulated" = "5min",
        signal?: AbortSignal,
        options?: {
            forceRefresh?: boolean;
            isPriorityRefresh?: boolean;
            underlyingsOverride?: string[];
            sourceOverride?: "rtl_legacy" | "stable";
            persist?: string;
            strikeRangeOverride?: string;
            expiryRangeOverride?: string;
        },
    ): Promise<ScannerData[]> {
        console.log(
            "🔍 [DATA SERVICE] fetchRawData called with dataMode:",
            dataMode,
            "sourceOverride:",
            options?.sourceOverride,
        );

        // If user explicitly requests stable data, use stable fetch
        if (options?.sourceOverride === "stable") {
            console.log(
                "🔍 [DATA SERVICE] Fetching STABLE data from Theta Terminal",
            );
            return this.fetchStableData(signal, options);
        }

        // Default to legacy RTL data
        console.log(
            "🔍 [DATA SERVICE] Using legacy RTL data method (centralized service is for Polygon tools only)",
        );
        return this.fetchRawDataLegacy(dataMode, signal, options);
    }

    /**
     * Fetch Greek/options data from Theta Terminal (stable pipeline).
     * Note: originally named fetchCboeGreeksData — now exclusively uses Theta.
     * When options.tableData is provided and has Gamma/OI for the underlyings, uses that.
     * Otherwise fetches from Theta Terminal via accumulated-data?source=stable.
     */
    async fetchCboeGreeksData(
        underlyings: string[],
        options?: {
            expiryRange?: string;
            expiryDate?: string;
            strikeRange?: string;
            strikeMin?: string;
            strikeMax?: string;
            tableData?: ScannerData[];
        },
    ): Promise<ScannerData[]> {
        const symbols = underlyings.length > 0 ? underlyings : ["SPY"];
        const wantSet = new Set(
            symbols.map((u) => String(u).trim().toUpperCase()),
        );
        if (options?.tableData && options.tableData.length > 0) {
            const withGreeks = options.tableData.filter((r) => {
                const u = String(
                    r.Underlying || (r as any).underlying_symbol || "",
                )
                    .trim()
                    .toUpperCase();
                return wantSet.has(u) && recordHasGammaOrOI(r);
            });
            if (withGreeks.length > 0) return this.cleanData(withGreeks);
        }
        const expiryRange =
            options?.expiryRange ??
            localStorage.getItem("mmec_expiry_range") ??
            "weekly";
        const exactExpiry =
            options?.expiryDate ??
            localStorage.getItem("mmec_expiry_date") ??
            "";
        const strikeParams =
            options?.strikeMin != null && options?.strikeMax != null
                ? {
                      strike_min: String(options.strikeMin),
                      strike_max: String(options.strikeMax),
                  }
                : options?.strikeRange !== undefined
                  ? { strike_range: options.strikeRange }
                  : coerceStrikeParamsForUnderlyings(symbols);
        const authToken = getAuthToken();
        let urlParams = `source=stable&underlyings=${encodeURIComponent(symbols.join(","))}`;
        if (expiryRange && expiryRange !== "") {
            urlParams += `&expiry_range=${encodeURIComponent(expiryRange)}`;
        }
        if (expiryRange === "specific-date" && exactExpiry) {
            urlParams += `&expiry=${encodeURIComponent(exactExpiry)}`;
        }
        if (
            strikeParams.strike_min != null &&
            strikeParams.strike_max != null
        ) {
            urlParams += `&strike_min=${encodeURIComponent(strikeParams.strike_min)}&strike_max=${encodeURIComponent(strikeParams.strike_max)}`;
        } else if (
            strikeParams.strike_range &&
            strikeParams.strike_range !== ""
        ) {
            urlParams += `&strike_range=${encodeURIComponent(strikeParams.strike_range)}`;
        }
        // "standard", not "lite": lite omits the OHLC snapshot, and OHLC is the only source
        // of session volume while the market is open. This table has a Volume column and a
        // premium column derived from it, so a lite fetch renders both as zero -- which is
        // not a cheaper answer, just a wrong one. Standard adds OHLC and still skips the
        // second-order greeks. The GEX chart views keep lite; they do not show volume.
        urlParams += "&fields=standard";
        const fullUrl = withSnapshotDate(getApiUrl(`/api/accumulated-data?${urlParams}`));
        const response = await fetch(fullUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            },
        });
        if (response.status === 401) {
            forceSessionExpired();
            throw new Error("Session ended. Use your sign-in link again.");
        }
        if (!response.ok) {
            throw new Error(`CBOE Greeks fetch failed: ${response.status}`);
        }
        const result = await response.json();
        if (result?.error) throw new Error(result.error);
        const dataArray = Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result)
              ? result
              : [];
        return this.cleanData(dataArray);
    }

    // Initialize centralized data service
    private async initializeCentralizedService(): Promise<void> {
        console.log(
            "🔧 [DATA SERVICE] Initializing centralized data service...",
        );

        // Subscribe to data updates
        centralizedDataService.subscribe((packet: DataPacket) => {
            console.log("📦 [DATA SERVICE] Received data packet:", {
                records: packet.data.length,
                timestamp: packet.timestamp,
                scanStatus: packet.scanStatus,
            });

            // Update local data
            this.data = packet.data.map((record: ScannerData) =>
                applyHeavyFieldsSafe(record),
            );
            this.lastFetch = new Date();
        });

        // Initialize with default config
        await centralizedDataService.initialize(DEFAULT_SCAN_CONFIG);
        console.log("✅ [DATA SERVICE] Centralized service initialized");
    }

    /** Fetch data from stable accumulate-data endpoint (Theta Terminal). */
    private async fetchStableData(
        signal?: AbortSignal,
        options?: {
            underlyingsOverride?: string[];
            strikeRangeOverride?: string;
            expiryRangeOverride?: string;
        },
    ): Promise<ScannerData[]> {
        try {
            const authToken = getAuthToken();
            // Get underlyings from localStorage or use provided override
            let underlyings = options?.underlyingsOverride || [];
            if (!underlyings.length) {
                const stored = localStorage.getItem("mmec_underlyings");
                underlyings = stored
                    ? stored.split(",").map((s) => s.trim())
                    : ["SPY"];
            }
            // Get strike range and expiry range from localStorage, unless the caller
            // forces one (the alerts feed forces strike_range=all so alerts never miss
            // a strike regardless of the table's zoom).
            const strikeMode = (
                options?.strikeRangeOverride
                    ? "override"
                    : localStorage.getItem("mmec_strike_mode") || "count"
            ).toLowerCase();
            let strikeParams = "";
            if (strikeMode === "override") {
                strikeParams = `strike_range=${encodeURIComponent(options!.strikeRangeOverride!)}`;
            } else if (strikeMode === "all") {
                strikeParams = "strike_range=all";
            } else if (strikeMode === "custom") {
                const sMin = (
                    localStorage.getItem("mmec_strike_min") || ""
                ).trim();
                const sMax = (
                    localStorage.getItem("mmec_strike_max") || ""
                ).trim();
                if (sMin && sMax) {
                    strikeParams = `strike_min=${encodeURIComponent(sMin)}&strike_max=${encodeURIComponent(sMax)}`;
                } else {
                    strikeParams = `strike_range=${encodeURIComponent(localStorage.getItem("mmec_strike_range") || "20")}`;
                }
            } else {
                strikeParams = `strike_range=${encodeURIComponent(localStorage.getItem("mmec_strike_range") || "20")}`;
            }
            const expiryRange =
                options?.expiryRangeOverride ||
                localStorage.getItem("mmec_expiry_range") ||
                "weekly";
            const expiryDate = (
                localStorage.getItem("mmec_expiry_date") || ""
            ).trim();

            const baseUrl = (() => {
                try {
                    return (window as any).__MMEC_BACKEND_URL || "/api";
                } catch {
                    return "/api";
                }
            })();

            let expiryParams = `expiry_range=${encodeURIComponent(expiryRange)}`;
            if (
                (expiryRange === "specific-date" || expiryRange === "custom") &&
                expiryDate
            ) {
                expiryParams += `&expiry=${encodeURIComponent(expiryDate)}`;
            }

            // The "as of" date has to travel with the request or the control does nothing.
            // This path builds its own URL and omitted it entirely, which is why picking a
            // past date returned the live chain no matter what was selected.
            const url = withSnapshotDate(
                `${baseUrl}/accumulated-data?source=stable&underlyings=${encodeURIComponent(
                    underlyings.join(","),
                )}&${strikeParams}&${expiryParams}`,
            );

            console.log(
                "🔍 [DATA SERVICE] Fetching stable data from:",
                url,
            );

            const response = await fetch(url, { signal });
            if (!response.ok) {
                throw new Error(
                    `Stable API returned ${response.status}`,
                );
            }

            const json = await response.json();
            // Ranged fetches arrive progressively (partial + pending_expiries); keep the
            // progress of this answer so the caller can poll until every expiry is in.
            const progress = extractStableProgress(url, json);
            setLastStableProgress(progress);
            if (!Array.isArray(json?.data)) {
                console.error(
                    "❌ [DATA SERVICE] Stable API response has no data array",
                    json,
                );
                return [];
            }

            // Normalize the stable data through dataService normalization
            const normalized = json.data.map((record: any) => {
                const out = { ...record };
                // Apply stable-specific normalization (already done by backend, but ensure fields are correct)
                const vol = safeNum(out["Volume"]);
                const lastPx = safeNum(out["Last"]);
                const bidPx = safeNum(out["Bid"]);
                const askPx = safeNum(out["Ask"]);
                const effLast =
                    lastPx > 0
                        ? lastPx
                        : bidPx > 0 && askPx > 0
                          ? (bidPx + askPx) / 2
                          : 0;
                const oi = safeNum(out["Open Interest"]);

                // Calculate premium fields from volume
                out["Total Volume"] = vol;
                if (
                    out["Total Premium"] === undefined ||
                    out["Total Premium"] === null
                ) {
                    out["Total Premium"] =
                        effLast > 0 ? vol * effLast * 100 : 0;
                }
                if (
                    out["Overall Premium"] === undefined ||
                    out["Overall Premium"] === null
                ) {
                    out["Overall Premium"] =
                        effLast > 0 ? vol * effLast * 100 : 0;
                }
                if (
                    out["Open Interest Dollar"] === undefined ||
                    out["Open Interest Dollar"] === null
                ) {
                    out["Open Interest Dollar"] =
                        effLast > 0 ? oi * effLast * 100 : 0;
                }
                if (
                    out["Fresh Volume %"] === undefined ||
                    out["Fresh Volume %"] === null
                ) {
                    out["Fresh Volume %"] =
                        oi > 0 ? (vol / oi) * 100 : 0;
                }
                if (
                    out["Vol/OI"] === undefined ||
                    out["Vol/OI"] === null
                ) {
                    out["Vol/OI"] = oi > 0 ? vol / oi : 0;
                }
                return out;
            });

            console.log(
                "✅ [DATA SERVICE] Fetched stable data:",
                normalized.length,
                "rows",
            );
            // Carried on the array itself (non-enumerable) so a concurrent fetch for other
            // symbols cannot be mistaken for this one's progress. Read with stableProgressOf().
            if (progress) {
                Object.defineProperty(normalized, "__stableProgress", {
                    value: progress,
                    enumerable: false,
                });
            }
            return normalized;
        } catch (error) {
            if (
                error instanceof DOMException &&
                error.name === "AbortError"
            ) {
                console.log(
                    "ℹ️  [DATA SERVICE] Stable fetch was cancelled",
                );
            } else {
                console.error(
                    "❌ [DATA SERVICE] Stable fetch failed:",
                    error,
                );
            }
            return [];
        }
    }

    // Legacy fetch method as fallback (optional AbortSignal for cancel)
    // RTL and stable run independently: RTL refresh every 10s is never blocked by stable/CBOE fetches.
    private async fetchRawDataLegacy(
        dataMode: "5min" | "accumulated" = "5min",
        signal?: AbortSignal,
        options?: {
            forceRefresh?: boolean;
            underlyingsOverride?: string[];
            isPriorityRefresh?: boolean;
            sourceOverride?: "rtl_legacy" | "stable";
            persist?: string;
        },
    ): Promise<ScannerData[]> {
        console.log("🔍 [DATA SERVICE] Using legacy fetch method");

        // Legacy RTL = source=rtl_legacy (Excel only). Stable = DB/Theta.
        const stored = localStorage.getItem("mmec_data_source") || "rtl_legacy";
        const normalizedStored = stored === "rtl" ? "rtl_legacy" : stored;
        const sourceParam =
            options?.sourceOverride ??
            (normalizedStored === "stable" ? "stable" : "rtl_legacy");
        if (sourceParam !== "rtl_legacy") this.lastRtlLegacyTimeoutAt = null;
        if (options?.sourceOverride) {
            console.log(
                "🔍 [DATA SERVICE] Source (explicit):",
                sourceParam,
                sourceParam === "rtl_legacy" ? "(Excel only)" : "",
            );
        }
        // After a Legacy RTL transient failure, skip refetch for RTL_LEGACY_BACKOFF_MS (avoid retry storm during edge restarts).
        if (
            sourceParam === "rtl_legacy" &&
            this.lastRtlLegacyTimeoutAt != null &&
            Date.now() - this.lastRtlLegacyTimeoutAt <
                DataService.RTL_LEGACY_BACKOFF_MS
        ) {
            console.log(
                "🔍 [DATA SERVICE] Legacy RTL in backoff — returning cached snapshot",
            );
            return this.data;
        }

        // Get selected underlyings: override (e.g. priority refresh) or from storage (Stable: session per tab, then local; RTL: local only)
        let selectedUnderlyings: string[] = options?.underlyingsOverride ?? [];
        if (selectedUnderlyings.length === 0) {
            if (sourceParam === "stable") {
                selectedUnderlyings = readStableUnderlyingsSessionFirst();
            } else if (typeof localStorage !== "undefined") {
                const savedUnderlyings = localStorage.getItem(
                    "mmec_selected_underlyings",
                );
                if (savedUnderlyings) {
                    try {
                        selectedUnderlyings = JSON.parse(savedUnderlyings);
                    } catch {
                        const single =
                            localStorage.getItem("mmec_selected_underlying") ||
                            "";
                        if (single) selectedUnderlyings = [single];
                    }
                }
            }
        }

        // Stable (CBOE): no pre-load. User must use "Stable fetch" bar (or priority refresh with override).
        if (selectedUnderlyings.length === 0 && sourceParam === "stable") {
            console.log(
                "🔍 [DATA SERVICE] Stable source with no underlying selected - returning empty (use Fetch bar)",
            );
            this.data = [];
            return [];
        }

        // Flow (live tape) is its own model: Options Flow → Live Flow uses /api/options-flow/flow (DB), not the data table.

        const isPriorityRefresh =
            (options?.underlyingsOverride?.length ?? 0) > 0 ||
            options?.isPriorityRefresh === true;
        // Only skip when the SAME source is already loading (RTL vs stable are independent). Priority refresh runs concurrently.
        if (!isPriorityRefresh) {
            if (sourceParam === "rtl_legacy" && this.isLoadingRtl) {
                console.log(
                    "🔍 [DATA SERVICE] RTL already in progress, returning cached data:",
                    this.data.length,
                );
                return this.data;
            }
            if (sourceParam === "stable" && this.isLoadingStable) {
                console.log(
                    "🔍 [DATA SERVICE] Stable already in progress, returning cached data:",
                    this.data.length,
                );
                return this.data;
            }
            if (sourceParam === "rtl_legacy") this.isLoadingRtl = true;
            else this.isLoadingStable = true;
            this.isLoading = true;
            this.error = null;
        }

        try {
            // Legacy RTL: dedicated endpoint (no cache, dedicated worker). Stable: accumulated-data.
            const endpoint =
                sourceParam === "rtl_legacy"
                    ? "/api/legacy-rtl/data"
                    : "/api/accumulated-data";

            // Get auth token with fallback to test token for development
            const authToken = getAuthToken();
            if (authToken)
                console.log(
                    "🔍 [DATA SERVICE] Using auth token:",
                    String(authToken).substring(0, 10) + "...",
                );
            console.log(
                "🔍 [DATA SERVICE] Using data source:",
                sourceParam,
                sourceParam === "rtl_legacy" ? "(dedicated endpoint)" : "",
            );

            let fullUrl: string;
            if (sourceParam === "rtl_legacy") {
                fullUrl = getApiUrl(endpoint);
                if (options?.persist) {
                    fullUrl +=
                        (fullUrl.includes("?") ? "&" : "?") +
                        "persist=" +
                        encodeURIComponent(options.persist);
                }
            } else {
                // Build URL with parameters for stable
                let urlParams = `source=${sourceParam}`;
                if (sourceParam === "stable") {
                    if (selectedUnderlyings.length > 0) {
                        urlParams += `&underlyings=${encodeURIComponent(selectedUnderlyings.join(","))}`;
                        console.log(
                            "🔍 [DATA SERVICE] CBOE underlying filter:",
                            selectedUnderlyings.join(","),
                        );
                    }
                    const expiryParams = getExpiryParams();
                    if (expiryParams.expiry_range) {
                        urlParams += `&expiry_range=${encodeURIComponent(expiryParams.expiry_range)}`;
                    }
                    if (expiryParams.expiry) {
                        urlParams += `&expiry=${encodeURIComponent(expiryParams.expiry)}`;
                    }
                    const strikeParams =
                        coerceStrikeParamsForUnderlyings(selectedUnderlyings);
                    if (
                        strikeParams.strike_min != null &&
                        strikeParams.strike_max != null
                    ) {
                        urlParams += `&strike_min=${encodeURIComponent(strikeParams.strike_min)}&strike_max=${encodeURIComponent(strikeParams.strike_max)}`;
                    } else if (strikeParams.strike_range) {
                        urlParams += `&strike_range=${encodeURIComponent(strikeParams.strike_range)}`;
                    }
                    if (options?.forceRefresh) urlParams += "&force_refresh=1";
                    // snapshot_date: fetch historical snapshot from warm-DB (QuestDB).
                    // Set by the "As of" datetime picker in StableDataFetchBar.
                    const snapDate = localStorage.getItem("mmec_snapshot_date");
                    if (snapDate) urlParams += `&snapshot_date=${encodeURIComponent(snapDate)}`;
                    // warm-DB backend: fields=lite skips OHLC + second_order
                    urlParams += "&fields=lite";
                    console.log(
                        "🔍 [DATA SERVICE] CBOE expiry:",
                        expiryParams.expiry
                            ? `specific-date:${expiryParams.expiry}`
                            : expiryParams.expiry_range || "weekly",
                        "strike:",
                        strikeParams.strike_min != null
                            ? `${strikeParams.strike_min}-${strikeParams.strike_max}`
                            : strikeParams.strike_range || "20",
                    );
                }
                fullUrl = getApiUrl(`${endpoint}?${urlParams}`);
            }
            console.log("🔍 [DATA SERVICE] Fetching from URL:", fullUrl);

            // Legacy RTL: on 404 (old backend) or 502 (bad gateway) retry with accumulated-data so old prod or down backend still tries fallback
            const isLegacyRtl = sourceParam === "rtl_legacy";
            const fallbackUrl = isLegacyRtl
                ? getApiUrl("/api/accumulated-data?source=rtl_legacy")
                : null;

            // Legacy RTL: 120s. Stable: 180s.
            const dataTimeoutMs = sourceParam === "stable" ? 180000 : 120000;
            const combined = new AbortController();
            const timeoutId = setTimeout(() => combined.abort(), dataTimeoutMs);
            if (signal) {
                signal.addEventListener(
                    "abort",
                    () => {
                        clearTimeout(timeoutId);
                        combined.abort();
                    },
                    { once: true },
                );
            }
            let response: Response;
            try {
                response = await fetch(fullUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        ...(authToken
                            ? { Authorization: `Bearer ${authToken}` }
                            : {}),
                    },
                    signal: combined.signal,
                });
                if (
                    isLegacyRtl &&
                    fallbackUrl &&
                    (response.status === 404 || response.status === 502)
                ) {
                    console.warn(
                        "🔍 [DATA SERVICE] Legacy RTL endpoint returned",
                        response.status,
                        "- retrying with accumulated-data",
                    );
                    response = await fetch(fallbackUrl, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            ...(authToken
                                ? { Authorization: `Bearer ${authToken}` }
                                : {}),
                        },
                        signal: combined.signal,
                    });
                }
            } catch (fetchErr) {
                clearTimeout(timeoutId);
                if ((fetchErr as Error)?.name === "AbortError") {
                    throw new Error(
                        signal?.aborted
                            ? "Cancelled"
                            : "Data request timed out. Check network or try again.",
                    );
                }
                throw fetchErr;
            }
            clearTimeout(timeoutId);

            console.log(
                "🔍 [DATA SERVICE] Response status:",
                response.status,
                response.statusText,
            );

            if (response.status === 401) {
                forceSessionExpired();
                throw new Error("Session ended. Use your sign-in link again.");
            }
            if (!response.ok) {
                let errMsg = `Request failed (${response.status})`;
                try {
                    const errText = await response.text();
                    if (errText && errText.trim()) {
                        const errJson = JSON.parse(errText);
                        if (typeof errJson?.error === "string")
                            errMsg = errJson.error;
                        else if (typeof errJson?.message === "string")
                            errMsg = errJson.message;
                    }
                } catch {
                    /* use default errMsg */
                }
                if (response.status >= 502 && response.status <= 504) {
                    if (sourceParam === "rtl_legacy" && this.data.length > 0) {
                        this.error =
                            "Legacy RTL temporarily unavailable — showing last snapshot.";
                        if (sourceParam === "rtl_legacy")
                            this.isLoadingRtl = false;
                        else this.isLoadingStable = false;
                        this.isLoading = false;
                        return this.data;
                    }
                    this.data = [];
                    this.error =
                        "Data temporarily unavailable. Please try again later.";
                    if (sourceParam === "rtl_legacy") this.isLoadingRtl = false;
                    else this.isLoadingStable = false;
                    this.isLoading = false;
                    return [];
                }
                throw new Error(errMsg);
            }

            let result: any;
            try {
                const text = await response.text();
                result = text && text.trim() ? JSON.parse(text) : {};
            } catch (e) {
                if (sourceParam === "rtl_legacy" && this.data.length > 0) {
                    this.error =
                        "Legacy RTL parse error — showing last snapshot.";
                    this.isLoadingRtl = false;
                    this.isLoading = false;
                    return this.data;
                }
                this.data = [];
                this.error = "Data temporarily unavailable.";
                console.warn(
                    "❌ [DATA SERVICE] Failed to parse accumulated-data response as JSON:",
                    e,
                );
                if (sourceParam === "rtl_legacy") this.isLoadingRtl = false;
                else this.isLoadingStable = false;
                this.isLoading = false;
                return [];
            }
            console.log(
                "🔍 [DATA SERVICE] Raw response:",
                result?.length ?? result?.total_records ?? "No length",
                typeof result,
            );
            if (result?.spot_gamma_fields) {
                console.log(
                    "🔍 [DATA SERVICE] Backend sent spot_gamma_fields (stable):",
                    result.spot_gamma_fields,
                );
            }

            if (
                result &&
                typeof result.error === "string" &&
                result.error.trim()
            ) {
                throw new Error(result.error.trim());
            }

            // Legacy RTL: backend returns success:false when Excel load fails (still HTTP 200)
            if (
                sourceParam === "rtl_legacy" &&
                result &&
                result.success === false
            ) {
                console.warn(
                    "❌ [DATA SERVICE] Legacy RTL success=false:",
                    result?.message ?? result,
                );
                if (!isPriorityRefresh) {
                    this.isLoadingRtl = false;
                    this.isLoading = false;
                }
                if (this.data.length > 0) {
                    console.log(
                        "🔍 [DATA SERVICE] RTL success=false — keeping last good cache",
                    );
                    return this.data;
                }
                console.warn(
                    "🔍 [DATA SERVICE] RTL success=false with no cache — returning empty",
                );
                return [];
            }

            // Store Level 2 totals for Legacy RTL and Stable.
            if (
                result &&
                (sourceParam === "rtl_legacy" || sourceParam === "stable")
            ) {
                this.lastLevel2Totals = {
                    total_bid_volume: Number(result.total_bid_volume) || 0,
                    total_ask_volume: Number(result.total_ask_volume) || 0,
                    total_open_interest:
                        result.total_open_interest != null
                            ? Number(result.total_open_interest)
                            : undefined,
                };
            } else {
                this.lastLevel2Totals = null;
            }

            // Clean the data to ensure no duplicate keys
            // Backend returns { success?, data: [...], total_records? } or sometimes just { data: [] }; accept data array regardless of success
            const dataArray = Array.isArray(result?.data)
                ? result.data
                : Array.isArray(result)
                  ? result
                  : [];

            if (sourceParam === "rtl_legacy" && dataArray.length === 0) {
                const msg =
                    typeof result?.message === "string" ? result.message : "";
                if (this.data.length > 0) {
                    console.warn(
                        "🔍 [DATA SERVICE] Legacy RTL empty response — keeping last good cache",
                    );
                    if (!isPriorityRefresh) {
                        this.isLoadingRtl = false;
                        this.isLoading = false;
                    }
                    return this.data;
                }
                if (/no scanner|scanner file/i.test(msg)) {
                    console.warn(
                        "🔍 [DATA SERVICE] No scanner file — keeping cached snapshot if any",
                    );
                    if (!isPriorityRefresh) {
                        this.isLoadingRtl = false;
                        this.isLoading = false;
                    }
                    return this.data.length > 0 ? this.data : [];
                }
            }

            // Debug: Log sample record to see what fields backend is sending
            if (dataArray.length > 0) {
                const sampleKeys = Object.keys(dataArray[0]);
                console.log(
                    "🔍 [DATA SERVICE] Sample record keys from backend:",
                    sampleKeys,
                );

                // Find Strike field by searching all variations
                const strikeKey = sampleKeys.find(
                    (k) =>
                        /strike/i.test(k) &&
                        !/accumelated|accumulated/i.test(k),
                );
                console.log(
                    "🔍 [DATA SERVICE] Found Strike key:",
                    strikeKey,
                    "Value:",
                    strikeKey ? dataArray[0][strikeKey] : "NOT FOUND",
                );

                // Log all keys that might be Strike
                const possibleStrikeKeys = sampleKeys.filter((k) =>
                    /strike/i.test(k),
                );
                console.log(
                    '🔍 [DATA SERVICE] All keys containing "strike":',
                    possibleStrikeKeys,
                );
            }

            const cleanedData = this.cleanData(dataArray);
            const readyForHeavy =
                sourceParam === "rtl_legacy"
                    ? prefillLegacyRtlForHeavyFields(cleanedData)
                    : cleanedData;

            // Debug: Check if Strike is present after cleaning
            if (readyForHeavy.length > 0) {
                const hasStrike =
                    "Strike" in readyForHeavy[0] &&
                    readyForHeavy[0].Strike !== null &&
                    readyForHeavy[0].Strike !== undefined;
                console.log(
                    "🔍 [DATA SERVICE] After cleaning - Strike present:",
                    hasStrike,
                    "Value:",
                    readyForHeavy[0].Strike,
                );
            }

            // Stable Theta: stableThetaChain preserves snapshot greeks (no row Source). RTL: full legacy heavy path.
            const stableGreekOpts: CalculateHeavyFieldsOptions =
                sourceParam === "stable" ? { stableThetaChain: true } : {};
            if (isPriorityRefresh) {
                this.lastFetch = new Date();
                const toReturn = readyForHeavy.map((record: ScannerData) =>
                    applyHeavyFieldsSafe(record, stableGreekOpts),
                );
                console.log(
                    `✅ [DATA SERVICE] Priority refresh: ${toReturn.length} records (not updating service cache)`,
                );
                return toReturn;
            }
            console.log(
                "🔢 [DATA SERVICE] Calculating derived fields for",
                readyForHeavy.length,
                "records...",
            );
            this.data = readyForHeavy.map((record: ScannerData) =>
                applyHeavyFieldsSafe(record, stableGreekOpts),
            );
            this.lastFetch = new Date();
            console.log(
                `✅ [DATA SERVICE] Data loaded: ${this.data.length} records (with calculations)`,
            );
            // Record views for top-50 most viewed (Discord bot uses this list)
            const underlyingsShown = Array.from(
                new Set(
                    this.data
                        .map((r) =>
                            (r.Underlying || (r as any).underlying_symbol || "")
                                .toString()
                                .trim()
                                .toUpperCase(),
                        )
                        .filter(Boolean),
                ),
            );
            trackUnderlyingViews(underlyingsShown);
            return this.data;
        } catch (error) {
            const raw =
                error instanceof Error
                    ? error.message
                    : "Unknown error occurred";
            const isTimeout =
                /data request timed out|timed out/i.test(raw) ||
                (error instanceof Error &&
                    (error as Error).name === "AbortError");
            const isNetwork =
                /failed to fetch|network error|load failed|networkrequestfailed|connection.*reset|err_connection|fetch failed/i.test(
                    raw,
                ) ||
                (error instanceof TypeError && raw === "Failed to fetch");
            // Same as timeout: connection resets (edge/backend restart) must not throw every refresh tick.
            if (sourceParam === "rtl_legacy" && (isTimeout || isNetwork)) {
                this.lastRtlLegacyTimeoutAt = Date.now();
                this.error = isTimeout
                    ? "Loading Legacy RTL data — server is busy, will retry automatically."
                    : "Loading Legacy RTL data — connection interrupted, will retry automatically.";
                if (!isPriorityRefresh) {
                    this.isLoadingRtl = false;
                    this.isLoading = false;
                }
                console.warn(
                    "❌ [DATA SERVICE] Legacy RTL transient failure; backing off.",
                    { isTimeout, isNetwork },
                );
                return this.data.length > 0 ? this.data : [];
            }
            this.error = isNetwork
                ? "Network error. Check connection or try again."
                : raw;
            if (!isNetwork)
                console.error(
                    "❌ [DATA SERVICE] Error fetching accumulated data:",
                    error,
                );
            else
                console.warn(
                    "❌ [DATA SERVICE] Network error fetching data:",
                    raw,
                );
            throw new Error(this.error);
        } finally {
            if (!isPriorityRefresh) {
                if (sourceParam === "rtl_legacy") this.isLoadingRtl = false;
                else this.isLoadingStable = false;
                this.isLoading = this.isLoadingRtl || this.isLoadingStable;
            }
        }
    }

    // Check if new data is available
    async checkForUpdates(): Promise<{
        has_updates: boolean;
        data_count: number;
        cache_age_seconds: number;
    }> {
        try {
            const authToken = getAuthToken();

            const response = await fetch(getApiUrl("/api/data/check-updates"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    ...(authToken
                        ? { Authorization: `Bearer ${authToken}` }
                        : {}),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.error) {
                throw new Error(result.error);
            }

            return {
                has_updates: result.has_updates || false,
                data_count: result.data_count || 0,
                cache_age_seconds: result.cache_age_seconds || 0,
            };
        } catch (error) {
            console.error("❌ Error checking for updates:", error);
            return { has_updates: false, data_count: 0, cache_age_seconds: 0 };
        }
    }

    // Force refresh data (bypass cache)
    async forceRefreshData(): Promise<ScannerData[]> {
        try {
            const authToken = getAuthToken();

            const response = await fetch(getApiUrl("/api/data/force-refresh"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    ...(authToken
                        ? { Authorization: `Bearer ${authToken}` }
                        : {}),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.error) {
                throw new Error(result.error);
            }

            // Clean the data to ensure no duplicate keys
            this.data = this.cleanData(result.data || []);
            this.lastFetch = new Date();
            console.log(
                `✅ Data force refreshed successfully: ${this.data.length} records`,
            );
            return this.data;
        } catch (error) {
            this.error =
                error instanceof Error
                    ? error.message
                    : "Unknown error occurred";
            console.error("❌ Error force refreshing data:", error);
            throw error;
        }
    }

    // Clean data to remove any duplicate keys and normalize field names
    private cleanData(data: any[]): ScannerData[] {
        if (!Array.isArray(data)) return [];

        // Field name mapping from Excel column names (exact match from backend logs) to standard frontend names
        const fieldMapping: { [key: string]: string } = {
            // Core fields - exact Excel column names
            Symbol: "Symbol",
            symbol: "Symbol",
            SYMBOL: "Symbol",

            Underlying: "Underlying",
            underlying: "Underlying",
            UNDERLYING: "Underlying",
            "Underlying Symbol": "Underlying",
            underlying_symbol: "Underlying",

            "Strike Price": "Strike", // Excel uses 'Strike Price', frontend expects 'Strike'
            Strike: "Strike",
            strike: "Strike",
            STRIKE: "Strike",
            strike_price: "Strike",

            "Option Type": "Option Type",
            option_type: "Option Type",
            "OPTION TYPE": "Option Type",
            Side: "Option Type",
            side: "Option Type",

            "Expiry Date": "Expiry Date",
            expiry_date: "Expiry Date",
            "EXPIRY DATE": "Expiry Date",
            "Expiration Date": "Expiry Date",
            expiration_date: "Expiry Date",

            "Underlying Price": "Underlying Price",
            underlying_price: "Underlying Price",
            "UNDERLYING PRICE": "Underlying Price",

            // Volume fields - exact Excel names
            "Bid Volume": "Bid Volume",
            bid_volume: "Bid Volume",
            bid_size: "Bid Volume",
            Bid_Size: "Bid Volume",
            BidSize: "Bid Volume",
            "BID VOLUME": "Bid Volume",
            "Bid Volume Accumulated": "Bid Volume accumelated", // Excel uses 'Accumulated', frontend uses 'accumelated'
            "Bid Volume accumelated": "Bid Volume accumelated",
            bid_volume_accumulated: "Bid Volume accumelated",
            bid_volume_accumelated: "Bid Volume accumelated",

            "Ask Volume": "Ask Volume",
            ask_volume: "Ask Volume",
            ask_size: "Ask Volume",
            Ask_Size: "Ask Volume",
            AskSize: "Ask Volume",
            "ASK VOLUME": "Ask Volume",
            "Ask Volume Accumulated": "Ask Volume accumelated",
            "Ask Volume accumelated": "Ask Volume accumelated",
            ask_volume_accumulated: "Ask Volume accumelated",
            ask_volume_accumelated: "Ask Volume accumelated",

            Volume: "Volume",
            volume: "Volume",
            VOLUME: "Volume",
            "Total Volume": "Total Volume",
            total_volume: "Total Volume",

            // Count fields - exact Excel names
            "Bid Count": "Bid Count",
            bid_count: "Bid Count",
            "BID COUNT": "Bid Count",
            "Bid Count Accumulated": "Bid Count accumelated",
            "Bid Count accumelated": "Bid Count accumelated",
            bid_count_accumulated: "Bid Count accumelated",
            bid_count_accumelated: "Bid Count accumelated",

            "Ask Count": "Ask Count",
            ask_count: "Ask Count",
            "ASK COUNT": "Ask Count",
            "Ask Count Accumulated": "Ask Count accumelated",
            "Ask Count accumelated": "Ask Count accumelated",
            ask_count_accumulated: "Ask Count accumelated",
            ask_count_accumelated: "Ask Count accumelated",

            Trades: "Trade Count", // Excel uses 'Trades', frontend expects 'Trade Count'
            "Trade Count": "Trade Count",
            trade_count: "Trade Count",
            "TRADE COUNT": "Trade Count",
            TRADES: "Trade Count",
            "Trades Accumulated": "Trades accumelated",
            "Trades accumelated": "Trades accumelated",
            trades_accumulated: "Trades accumelated",
            trades_accumelated: "Trades accumelated",

            // Price fields
            Last: "Last",
            last: "Last",
            LAST: "Last",
            last_price: "Last",

            Mid: "Mid",
            mid: "Mid",
            MID: "Mid",

            Bid: "Bid",
            bid: "Bid",
            BID: "Bid",

            Ask: "Ask",
            ask: "Ask",
            ASK: "Ask",

            // Greeks
            Delta: "Delta",
            delta: "Delta",
            DELTA: "Delta",
            "Delta accumelated": "Delta accumelated",
            "Delta Accumulated": "Delta accumelated",
            "Delta accumulated": "Delta accumelated", // CBOE/backend may use correct spelling
            "Delta ACC": "Delta accumelated",
            "Delta Acc": "Delta accumelated",
            Deltaacc: "Delta accumelated",
            delta_accumelated: "Delta accumelated",
            delta_accumulated: "Delta accumelated",
            "Delta ( ask - bid )": "Delta ( ask - bid )",
            "Delta(Ask - Bid )": "Delta ( ask - bid )",
            "Delta (Ask - Bid )": "Delta ( ask - bid )",
            "Delta (Ask-Bid)": "Delta ( ask - bid )",
            "Delta(Ask-Bid)": "Delta ( ask - bid )",
            "Delta Ask Bid": "Delta ( ask - bid )",
            delta_ask_bid: "Delta ( ask - bid )",

            Gamma: "Gamma",
            gamma: "Gamma",
            GAMMA: "Gamma",

            Vanna: "Vanna",
            vanna: "Vanna",
            VANNA: "Vanna",
            Charm: "Charm",
            charm: "Charm",
            CHARM: "Charm",

            Theta: "Theta",
            theta: "Theta",
            THETA: "Theta",

            Vega: "Vega",
            vega: "Vega",
            VEGA: "Vega",

            Rho: "Rho",
            rho: "Rho",
            RHO: "Rho",

            Lambda: "Lambda",
            lambda: "Lambda",
            LAMBDA: "Lambda",

            // Other fields - exact Excel names
            "Open Interest": "Open Interest",
            open_interest: "Open Interest",
            "OPEN INTEREST": "Open Interest",

            "Implied Volatility": "Implied Volatility",
            "Implied Vol": "Implied Volatility",
            implied_vol: "Implied Volatility",
            implied_volatility: "Implied Volatility",
            "IMPLIED VOLATILITY": "Implied Volatility",
            iv: "Implied Volatility",

            "Vol/Open Interest": "Vol/OI", // Excel uses 'Vol/Open Interest', frontend expects 'Vol/OI'
            "Vol/OI": "Vol/OI",
            vol_oi: "Vol/OI",
            "vol/open_interest": "Vol/OI",

            Moneyness: "Moneyness",
            moneyness: "Moneyness",
            MONEYNESS: "Moneyness",
            MoneynessCategory: "Moneyness",

            Direction: "Direction",
            direction: "Direction",
            DIRECTION: "Direction",
            "Direction accumelated": "Direction accumelated",
            "Direction Accumulated": "Direction accumelated",
            direction_accumelated: "Direction accumelated",
            direction_accumulated: "Direction accumelated",

            Sentiment: "Sentiment",
            sentiment: "Sentiment",
            SENTIMENT: "Sentiment",
            "Sentiment accumelated": "Sentiment accumelated",
            "Sentiment Accumulated": "Sentiment accumelated",
            sentiment_accumelated: "Sentiment accumelated",
            sentiment_accumulated: "Sentiment accumelated",

            // Calculated fields from backend (calculate_additional_fields)
            "Days left": "Days left",
            days_left: "Days left",
            "Days Left": "Days left",
            daysleft: "Days left",

            "Delta Premium": "Delta Premium",
            delta_premium: "Delta Premium",
            "Delta Accumulated Premium": "Delta Accumulated Premium",
            delta_accumulated_premium: "Delta Accumulated Premium",

            "Bid Volume Premium": "Bid Volume Premium",
            bid_volume_premium: "Bid Volume Premium",
            "Bid Accumulated Premium": "Bid Accumulated Premium",
            bid_accumulated_premium: "Bid Accumulated Premium",

            "Ask Volume Premium": "Ask Volume Premium",
            ask_volume_premium: "Ask Volume Premium",
            "Ask Accumulated Premium": "Ask Accumulated Premium",
            ask_accumulated_premium: "Ask Accumulated Premium",

            "Haltz (Volume Concentration)": "Haltz (Volume Concentration)",
            Haltz: "Haltz (Volume Concentration)",
            haltz: "Haltz (Volume Concentration)",
            HALTZ: "Haltz (Volume Concentration)",

            "Band (IV Indicator)": "Band (IV Indicator)",
            Band: "Band (IV Indicator)",
            band: "Band (IV Indicator)",
            BAND: "Band (IV Indicator)",

            "Mal (Gamma Indicator)": "Mal (Gamma Indicator)",
            Mal: "Mal (Gamma Indicator)",
            mal: "Mal (Gamma Indicator)",
            MAL: "Mal (Gamma Indicator)",

            "Zahn (Count Indicator)": "Zahn (Count Indicator)",
            Zahn: "Zahn (Count Indicator)",
            zahn: "Zahn (Count Indicator)",
            ZAHN: "Zahn (Count Indicator)",
            "Zahn Rank": "Zahn (Count Indicator)",
            "Haltz Rank": "Haltz (Volume Concentration)",
            "Band Rank": "Band (IV Indicator)",

            "Theoretical Value": "Theoretical Value",
            theoretical_value: "Theoretical Value",
            Theoretical: "Theoretical Value",

            "MMEC Gap": "MMEC Gap",
            mmec_gap: "MMEC Gap",
            Gap: "MMEC Gap",

            Source: "Source",
            source: "Source",
            time: "time",
            Time: "time",

            "Empty Field": "Empty Field", // Ignore this field
        };

        // Debug: Log first item's keys before mapping
        if (data.length > 0) {
            console.log(
                "🔍 [CLEAN DATA] First item keys before mapping:",
                Object.keys(data[0]),
            );
        }

        const cleanedData = data
            .map((item, index) => {
                try {
                    if (item === null || item === undefined) return null;
                    if (typeof item !== "object" || Array.isArray(item)) {
                        if (index < 5)
                            console.warn(
                                "[CLEAN DATA] skipping non-object row",
                                index,
                            );
                        return null;
                    }
                    const cleanedItem: any = {};
                    const seenKeys = new Set<string>();

                    for (const [key, value] of Object.entries(item)) {
                        if (value === null || value === undefined) continue;

                        // Normalize key (trim, handle spaces)
                        const trimmedKey = String(key).trim();

                        // Skip empty field
                        if (
                            trimmedKey === "Empty Field" ||
                            trimmedKey.toLowerCase() === "empty field"
                        ) {
                            continue;
                        }

                        // Map to standard field name - try multiple variations
                        let standardKey =
                            fieldMapping[trimmedKey] ||
                            fieldMapping[trimmedKey.toLowerCase()] ||
                            fieldMapping[trimmedKey.toUpperCase()] ||
                            trimmedKey;

                        // If still not found, try removing spaces and matching
                        if (
                            standardKey === trimmedKey &&
                            !fieldMapping[standardKey]
                        ) {
                            const noSpacesKey = trimmedKey.replace(/\s+/g, "");
                            standardKey =
                                fieldMapping[noSpacesKey] ||
                                fieldMapping[noSpacesKey.toLowerCase()] ||
                                trimmedKey;
                        }

                        // Pattern-based mappings for common variations
                        if (standardKey === trimmedKey) {
                            // Strike variations
                            if (
                                /strike/i.test(trimmedKey) &&
                                !/accumelated|accumulated/i.test(trimmedKey)
                            ) {
                                standardKey = "Strike";
                            }
                            // Trade Count variations
                            else if (
                                /^trades?$/i.test(trimmedKey) &&
                                !/accum/i.test(trimmedKey)
                            ) {
                                standardKey = "Trade Count";
                            }
                            // Vol/OI variations
                            else if (
                                /vol.*oi|vol.*open.*interest/i.test(trimmedKey)
                            ) {
                                standardKey = "Vol/OI";
                            }
                            // Accumulated -> accumelated (frontend typo)
                            else if (
                                /accumulated/i.test(trimmedKey) &&
                                !/accumelated/i.test(trimmedKey)
                            ) {
                                standardKey = trimmedKey.replace(
                                    /accumulated/i,
                                    "accumelated",
                                );
                            }
                            // Delta ACC aliases from some feeds/UIs
                            else if (/^delta[\s_-]*acc$/i.test(trimmedKey)) {
                                standardKey = "Delta accumelated";
                            }
                            // Delta ask-bid aliases with inconsistent spaces/brackets
                            else if (
                                /^delta\s*\(?\s*ask\s*[-/]\s*bid\s*\)?$/i.test(
                                    trimmedKey,
                                )
                            ) {
                                standardKey = "Delta ( ask - bid )";
                            }
                        }

                        // Final fallback - preserve original if no mapping found (but log it for first record)
                        if (
                            standardKey === trimmedKey &&
                            !fieldMapping[standardKey] &&
                            index === 0
                        ) {
                            console.warn(
                                `⚠️ [CLEAN DATA] No mapping found for field: "${trimmedKey}" - preserving as-is`,
                            );
                        }

                        // Use lowercase normalized key for duplicate detection
                        const normalizedKey = standardKey
                            .toLowerCase()
                            .replace(/\s+/g, "_");

                        // Normalize value for fields that differ between RTL and CBOE
                        let outValue = value;
                        if (
                            standardKey === "Option Type" &&
                            typeof value === "string"
                        ) {
                            const v = value.trim().toUpperCase();
                            if (v === "CALL" || v === "C") outValue = "C";
                            else if (v === "PUT" || v === "P") outValue = "P";
                        }
                        // CBOE sends "-" for missing numerics; coerce to number for all numeric fields used by DataTable/calculations
                        const numericFields = [
                            "Strike",
                            "Last",
                            "Underlying Price",
                            "Bid",
                            "Ask",
                            "Mid",
                            "Volume",
                            "Open Interest",
                            "Implied Volatility",
                            "Delta",
                            "Gamma",
                            "Theta",
                            "Vega",
                            "Rho",
                            "Lambda",
                            "Theoretical Value",
                            "Vanna",
                            "Charm",
                            "Bid Count",
                            "Ask Count",
                            "Bid Volume",
                            "Ask Volume",
                            "Trade Count",
                            "Bid Count accumelated",
                            "Ask Count accumelated",
                            "Bid Volume accumelated",
                            "Ask Volume accumelated",
                            "Trades accumelated",
                            "Delta ( ask - bid )",
                            "Delta accumelated",
                            "Delta Premium",
                            "Delta Accumulated Premium",
                            "Bid Volume Premium",
                            "Ask Volume Premium",
                            "Bid Accumulated Premium",
                            "Ask Accumulated Premium",
                            "Haltz (Volume Concentration)",
                            "Band (IV Indicator)",
                            "Mal (Gamma Indicator)",
                            "Zahn (Count Indicator)",
                            "Days left",
                            "MMEC Gap",
                        ];
                        if (numericFields.includes(standardKey)) {
                            if (
                                value === "-" ||
                                value === null ||
                                value === undefined ||
                                value === ""
                            ) {
                                outValue = 0;
                            } else if (
                                typeof value === "string" &&
                                value.trim() !== ""
                            ) {
                                const n = parseFloat(
                                    String(value).replace(/,/g, ""),
                                );
                                if (!Number.isNaN(n)) outValue = n;
                            }
                        }

                        if (!seenKeys.has(normalizedKey)) {
                            cleanedItem[standardKey] = outValue;
                            seenKeys.add(normalizedKey);
                        } else {
                            // Prefer canonical values: Option Type C/P over Side Call/Put; prefer number over string for numerics
                            if (
                                standardKey === "Option Type" &&
                                (outValue === "C" || outValue === "P")
                            ) {
                                cleanedItem[standardKey] = outValue;
                            } else if (
                                typeof outValue === "number" &&
                                typeof cleanedItem[standardKey] === "string" &&
                                (standardKey === "Strike" ||
                                    standardKey === "Last" ||
                                    standardKey === "Underlying Price")
                            ) {
                                cleanedItem[standardKey] = outValue;
                            } else if (numericFields.includes(standardKey)) {
                                // Multiple raw keys map to one column (e.g. implied_vol vs implied_volatility). Prefer later
                                // non-zero value over an earlier zero (fixes consolidated net gamma / IV when order varies).
                                const curN = safeNum(cleanedItem[standardKey]);
                                const newN = safeNum(outValue);
                                const curRaw = cleanedItem[standardKey];
                                const curWeak =
                                    !(
                                        Number.isFinite(curN) &&
                                        Math.abs(curN) > 0.000000000001
                                    ) ||
                                    curRaw === "" ||
                                    curRaw === "-";
                                const newStrong =
                                    Number.isFinite(newN) &&
                                    Math.abs(newN) > 0.000000000001;
                                if (curWeak && newStrong) {
                                    cleanedItem[standardKey] = outValue;
                                }
                            }
                            if (
                                index === 0 &&
                                !(
                                    numericFields.includes(standardKey) &&
                                    Math.abs(safeNum(outValue)) > 0.000000000001
                                )
                            ) {
                                console.warn(
                                    `⚠️ [CLEAN DATA] Duplicate key skipped: "${trimmedKey}" (mapped to "${standardKey}")`,
                                );
                            }
                        }
                    }

                    // Post-pass: ensure Option Type is always C or P (CBOE may send Call/Put)
                    const ot = cleanedItem["Option Type"];
                    if (
                        ot !== undefined &&
                        ot !== null &&
                        typeof ot === "string"
                    ) {
                        const v = String(ot).trim().toUpperCase();
                        if (v === "CALL" || v === "C")
                            cleanedItem["Option Type"] = "C";
                        else if (v === "PUT" || v === "P")
                            cleanedItem["Option Type"] = "P";
                    }
                    // Ensure Strike is number (both RTL and CBOE)
                    if (
                        cleanedItem["Strike"] !== undefined &&
                        cleanedItem["Strike"] !== null
                    ) {
                        const s = parseFloat(
                            String(cleanedItem["Strike"]).replace(/,/g, ""),
                        );
                        cleanedItem["Strike"] = Number.isNaN(s) ? 0 : s;
                    }
                    // Ensure DataTable-critical fields exist (RTL/CBOE alignment: avoid blank or broken rows)
                    if (
                        cleanedItem["Underlying"] === undefined ||
                        cleanedItem["Underlying"] === null ||
                        String(cleanedItem["Underlying"]).trim() === ""
                    ) {
                        cleanedItem["Underlying"] =
                            cleanedItem["Symbol"] != null
                                ? String(cleanedItem["Symbol"])
                                : "Unknown";
                    }
                    if (
                        cleanedItem["Strike"] === undefined ||
                        cleanedItem["Strike"] === null
                    ) {
                        cleanedItem["Strike"] = 0;
                    }
                    if (
                        cleanedItem["Option Type"] === undefined ||
                        cleanedItem["Option Type"] === null ||
                        cleanedItem["Option Type"] === ""
                    ) {
                        cleanedItem["Option Type"] = "C";
                    }

                    for (const nk of Object.keys(cleanedItem)) {
                        const vv = cleanedItem[nk];
                        if (typeof vv === "number" && !Number.isFinite(vv))
                            cleanedItem[nk] = 0;
                    }

                    return cleanedItem as ScannerData;
                } catch (rowErr) {
                    if (index < 10)
                        console.warn(
                            "[CLEAN DATA] row normalize failed",
                            index,
                            rowErr,
                        );
                    return null;
                }
            })
            .filter((row): row is ScannerData => row != null);

        // Debug: Log first item's keys after mapping
        if (cleanedData.length > 0) {
            console.log(
                "🔍 [CLEAN DATA] First item keys after mapping:",
                Object.keys(cleanedData[0]),
            );
            console.log(
                "🔍 [CLEAN DATA] First item Strike value:",
                cleanedData[0].Strike,
            );
        }

        return cleanedData;
    }

    getProcessedData(): ScannerData[] {
        return this.data;
    }

    getLastFetch(): Date | null {
        return this.lastFetch;
    }

    getLoadingStatus(): boolean {
        return this.isLoading;
    }

    getError(): string | null {
        return this.error;
    }

    // Get chart title based on chart type
    getChartTitle(chartType: ChartType): string {
        const titles: Record<ChartType, string> = {
            [ChartType.VOLUME_BY_SYMBOL]: "Volume by Symbol",
            [ChartType.VOLUME_BY_STRIKE]: "Volume by Strike",
            [ChartType.DELTA_BY_SYMBOL]: "Delta by Symbol",
            [ChartType.DELTA_BY_STRIKE]: "Delta by Strike",
            [ChartType.IV_BY_SYMBOL]: "Implied Volatility by Symbol",
            [ChartType.IV_BY_STRIKE]: "Implied Volatility by Strike",
            [ChartType.SENTIMENT_BY_SYMBOL]: "Sentiment by Symbol",
            [ChartType.SENTIMENT_BY_STRIKE]: "Sentiment by Strike",
            [ChartType.HALTZ_BY_SYMBOL]: "Haltz by Symbol",
            [ChartType.BAND_BY_SYMBOL]: "Band by Symbol",
            [ChartType.ZAHN_BY_SYMBOL]: "Zahn by Symbol",
            [ChartType.MONEYNESS_DISTRIBUTION]: "Moneyness Distribution",
            [ChartType.OPTION_TYPE_DISTRIBUTION]: "Option Type Distribution",
            [ChartType.VOLUME_DELTA_CORRELATION]: "Volume-Delta Correlation",
            [ChartType.IV_VOLUME_CORRELATION]: "IV-Volume Correlation",
            [ChartType.STRIKE_VOLUME_HEATMAP]: "Strike-Volume Heatmap",
            [ChartType.EXPIRY_VOLUME_HEATMAP]: "Expiry-Volume Heatmap",
            [ChartType.UNDERLYING_SENTIMENT]: "Underlying Sentiment",
            [ChartType.OPTION_STRATEGY_DISTRIBUTION]:
                "Option Strategy Distribution",
            [ChartType.DIRECTION_ANALYSIS]: "Direction Analysis",
            [ChartType.ACCUMULATED_ANALYSIS]: "Accumulated Analysis",
            [ChartType.FIVE_MINUTE_ANALYSIS]: "5-Minute Analysis",
            [ChartType.COMPREHENSIVE_ANALYSIS]: "Comprehensive Analysis",
        };
        return titles[chartType] || "Unknown Chart";
    }

    // Get data for specific chart type
    getChartData(chartType: ChartType): any[] {
        switch (chartType) {
            case ChartType.VOLUME_BY_SYMBOL:
                return this.getVolumeBySymbolData();
            case ChartType.VOLUME_BY_STRIKE:
                return this.getVolumeByStrikeData();
            case ChartType.DELTA_BY_SYMBOL:
                return this.getDeltaBySymbolData();
            case ChartType.IV_BY_SYMBOL:
                return this.getIVBySymbolData();
            case ChartType.SENTIMENT_BY_SYMBOL:
                return this.getSentimentBySymbolData();
            case ChartType.HALTZ_BY_SYMBOL:
                return this.getHaltzBySymbolData();
            case ChartType.BAND_BY_SYMBOL:
                return this.getBandBySymbolData();
            case ChartType.ZAHN_BY_SYMBOL:
                return this.getZahnBySymbolData();
            case ChartType.MONEYNESS_DISTRIBUTION:
                return this.getMoneynessDistributionData();
            case ChartType.OPTION_TYPE_DISTRIBUTION:
                return this.getOptionTypeDistributionData();
            default:
                return this.data;
        }
    }

    private getVolumeBySymbolData(): any[] {
        const volumeBySymbol = new Map<string, number>();

        this.data.forEach((item) => {
            const symbol = this.getFieldValue(item, "underlying");
            const volume = this.getFieldValue(item, "volume") || 0;

            if (symbol) {
                volumeBySymbol.set(
                    symbol,
                    (volumeBySymbol.get(symbol) || 0) + volume,
                );
            }
        });

        return Array.from(volumeBySymbol.entries()).map(([symbol, volume]) => ({
            symbol,
            volume,
        }));
    }

    private getVolumeByStrikeData(): any[] {
        const volumeByStrike = new Map<number, number>();

        this.data.forEach((item) => {
            const strike = this.getFieldValue(item, "strike");
            const volume = this.getFieldValue(item, "volume") || 0;

            if (strike && !isNaN(strike)) {
                volumeByStrike.set(
                    strike,
                    (volumeByStrike.get(strike) || 0) + volume,
                );
            }
        });

        return Array.from(volumeByStrike.entries()).map(([strike, volume]) => ({
            strike,
            volume,
        }));
    }

    private getDeltaBySymbolData(): any[] {
        const deltaBySymbol = new Map<
            string,
            { totalDelta: number; count: number }
        >();

        this.data.forEach((item) => {
            const symbol = this.getFieldValue(item, "underlying");
            const delta = this.getFieldValue(item, "delta") || 0;

            if (symbol && !isNaN(delta)) {
                const existing = deltaBySymbol.get(symbol) || {
                    totalDelta: 0,
                    count: 0,
                };
                existing.totalDelta += Math.abs(delta);
                existing.count += 1;
                deltaBySymbol.set(symbol, existing);
            }
        });

        return Array.from(deltaBySymbol.entries()).map(
            ([symbol, { totalDelta, count }]) => ({
                symbol,
                averageDelta: count > 0 ? totalDelta / count : 0,
            }),
        );
    }

    private getIVBySymbolData(): any[] {
        const ivBySymbol = new Map<
            string,
            { totalIV: number; count: number }
        >();

        this.data.forEach((item) => {
            const symbol = this.getFieldValue(item, "underlying");
            const iv = this.getFieldValue(item, "impliedVolatility") || 0;

            if (symbol && !isNaN(iv)) {
                const existing = ivBySymbol.get(symbol) || {
                    totalIV: 0,
                    count: 0,
                };
                existing.totalIV += iv;
                existing.count += 1;
                ivBySymbol.set(symbol, existing);
            }
        });

        return Array.from(ivBySymbol.entries()).map(
            ([symbol, { totalIV, count }]) => ({
                symbol,
                averageIV: count > 0 ? totalIV / count : 0,
            }),
        );
    }

    private getSentimentBySymbolData(): any[] {
        const sentimentBySymbol = new Map<
            string,
            { bullish: number; bearish: number; neutral: number }
        >();

        this.data.forEach((item) => {
            const symbol = this.getFieldValue(item, "underlying");
            const sentiment =
                this.getFieldValue(item, "properSentiment") || "Neutral";

            if (symbol) {
                const existing = sentimentBySymbol.get(symbol) || {
                    bullish: 0,
                    bearish: 0,
                    neutral: 0,
                };

                if (sentiment.toLowerCase().includes("bullish")) {
                    existing.bullish += 1;
                } else if (sentiment.toLowerCase().includes("bearish")) {
                    existing.bearish += 1;
                } else {
                    existing.neutral += 1;
                }

                sentimentBySymbol.set(symbol, existing);
            }
        });

        return Array.from(sentimentBySymbol.entries()).map(
            ([symbol, counts]) => ({
                symbol,
                ...counts,
            }),
        );
    }

    private getHaltzBySymbolData(): any[] {
        const haltzBySymbol = new Map<
            string,
            { totalHaltz: number; count: number }
        >();

        this.data.forEach((item) => {
            const symbol = this.getFieldValue(item, "underlying");
            const haltz = this.getFieldValue(item, "haltz") || 0;

            if (symbol && !isNaN(haltz)) {
                const existing = haltzBySymbol.get(symbol) || {
                    totalHaltz: 0,
                    count: 0,
                };
                existing.totalHaltz += Math.abs(haltz);
                existing.count += 1;
                haltzBySymbol.set(symbol, existing);
            }
        });

        return Array.from(haltzBySymbol.entries()).map(
            ([symbol, { totalHaltz, count }]) => ({
                symbol,
                averageHaltz: count > 0 ? totalHaltz / count : 0,
            }),
        );
    }

    private getBandBySymbolData(): any[] {
        const bandBySymbol = new Map<
            string,
            { totalBand: number; count: number }
        >();

        this.data.forEach((item) => {
            const symbol = this.getFieldValue(item, "underlying");
            const band = this.getFieldValue(item, "band") || 0;

            if (symbol && !isNaN(band)) {
                const existing = bandBySymbol.get(symbol) || {
                    totalBand: 0,
                    count: 0,
                };
                existing.totalBand += Math.abs(band);
                existing.count += 1;
                bandBySymbol.set(symbol, existing);
            }
        });

        return Array.from(bandBySymbol.entries()).map(
            ([symbol, { totalBand, count }]) => ({
                symbol,
                averageBand: count > 0 ? totalBand / count : 0,
            }),
        );
    }

    // Real-time price updates
    startRealTimeUpdates(): void {
        const underlyings = SymbolSeparationService.getUniqueUnderlyings(
            this.data,
        );
        console.log(
            "🔄 [DATA SERVICE] Starting real-time updates for:",
            underlyings,
        );

        realTimePriceService.start(underlyings, 1000); // Update every second

        // Subscribe to price updates
        realTimePriceService.subscribe((prices) => {
            console.log(
                "📈 [DATA SERVICE] Received price updates:",
                prices.size,
                "symbols",
            );
            this.updateDataWithRealTimePrices();
        });
    }

    stopRealTimeUpdates(): void {
        realTimePriceService.stop();
        console.log("⏹️ [DATA SERVICE] Stopped real-time updates");
    }

    // Update data with real-time prices
    private updateDataWithRealTimePrices(): void {
        this.data = realTimePriceService.updateScannerDataWithPrices(this.data);
        console.log("🔄 [DATA SERVICE] Updated data with real-time prices");
    }

    // Get separated data by underlying symbol
    getSeparatedData(): SeparatedData {
        return SymbolSeparationService.separateByUnderlying(this.data);
    }

    // Get data for specific underlying
    getDataForUnderlying(underlying: string): ScannerData[] {
        return SymbolSeparationService.filterByUnderlying(
            this.data,
            underlying,
        );
    }

    // Get strike ranges for indicator pane
    getStrikeRanges(): Record<
        string,
        { min: number; max: number; strikes: number[] }
    > {
        const separatedData = this.getSeparatedData();
        return SymbolSeparationService.getPriceRanges(separatedData);
    }

    // Get summary statistics
    getSummaryStats(): Record<string, any> {
        const separatedData = this.getSeparatedData();
        return SymbolSeparationService.getSummaryStats(separatedData);
    }

    // Check if real-time updates are running
    isRealTimeRunning(): boolean {
        return realTimePriceService.isServiceRunning();
    }

    // Get centralized service status
    getCentralizedServiceStatus() {
        return centralizedDataService.getStatistics();
    }

    // Get scan status
    getScanStatus() {
        return centralizedDataService.getScanStatus();
    }

    // Get market status
    getMarketStatus() {
        return centralizedDataService.getMarketStatus();
    }

    // Force a scan (admin only)
    async forceScan(): Promise<void> {
        return centralizedDataService.forceScan();
    }

    // Stop scanning
    stopScanning(): void {
        centralizedDataService.stopScanning();
    }

    // Update scan configuration
    async updateScanConfig(
        config: Partial<typeof DEFAULT_SCAN_CONFIG>,
    ): Promise<void> {
        return centralizedDataService.updateConfig(config);
    }

    // Check if centralized service is running
    isCentralizedServiceRunning(): boolean {
        return centralizedDataService.isRunning();
    }

    private getZahnBySymbolData(): any[] {
        const zahnBySymbol = new Map<
            string,
            { totalZahn: number; count: number }
        >();

        this.data.forEach((item) => {
            const symbol = this.getFieldValue(item, "underlying");
            const zahn = this.getFieldValue(item, "zahn") || 0;

            if (symbol && !isNaN(zahn)) {
                const existing = zahnBySymbol.get(symbol) || {
                    totalZahn: 0,
                    count: 0,
                };
                existing.totalZahn += Math.abs(zahn);
                existing.count += 1;
                zahnBySymbol.set(symbol, existing);
            }
        });

        return Array.from(zahnBySymbol.entries()).map(
            ([symbol, { totalZahn, count }]) => ({
                symbol,
                averageZahn: count > 0 ? totalZahn / count : 0,
            }),
        );
    }

    private getMoneynessDistributionData(): any[] {
        const distribution = { OTM: 0, ATM: 0, ITM: 0 };

        this.data.forEach((item) => {
            const moneyness =
                this.getFieldValue(item, "moneyness") || "Unknown";

            if (moneyness === "OTM") {
                distribution.OTM += 1;
            } else if (moneyness === "ATM") {
                distribution.ATM += 1;
            } else if (moneyness === "ITM") {
                distribution.ITM += 1;
            }
        });

        return Object.entries(distribution).map(([type, count]) => ({
            type,
            count,
        }));
    }

    private getOptionTypeDistributionData(): any[] {
        const distribution = { Call: 0, Put: 0 };

        this.data.forEach((item) => {
            const optionType =
                this.getFieldValue(item, "optionType") || "Unknown";

            if (optionType === "C" || optionType === "Call") {
                distribution.Call += 1;
            } else if (optionType === "P" || optionType === "Put") {
                distribution.Put += 1;
            }
        });

        return Object.entries(distribution).map(([type, count]) => ({
            type,
            count,
        }));
    }

    // Fallback list when API is unreachable (e.g. VPS/network) so the list never stays empty
    private static readonly FALLBACK_UNDERLYINGS = [
        "SPY",
        "SPX",
        "SPXW",
        "QQQ",
        "IWM",
        "AAPL",
        "AMZN",
        "GOOGL",
        "META",
        "MSFT",
        "NVDA",
        "TSLA",
    ];

    // In-memory cache for stable underlyings loaded from static file (never re-fetch once loaded)
    private static _stableUnderlyingsCache: string[] | null = null;

    // Fetch available underlyings. Stable loads from a pre-built static JSON (instant, no API call).
    async fetchAvailableUnderlyings(
        sourceOverride?: string,
    ): Promise<string[]> {
        const dataSourceRaw =
            sourceOverride ??
            localStorage.getItem("mmec_data_source") ??
            "rtl_legacy";
        const dataSource =
            dataSourceRaw === "rtl" ? "rtl_legacy" : dataSourceRaw;

        // Stable: load from static file bundled at build time (public/stable_underlyings.json).
        if (dataSource === "stable") {
            if (
                DataService._stableUnderlyingsCache &&
                DataService._stableUnderlyingsCache.length > 0
            ) {
                return DataService._stableUnderlyingsCache;
            }
            try {
                const r = await fetch(
                    `${process.env.PUBLIC_URL || ""}/stable_underlyings.json`,
                    { cache: "force-cache" },
                );
                if (r.ok) {
                    const list: string[] = await r.json();
                    if (Array.isArray(list) && list.length > 0) {
                        DataService._stableUnderlyingsCache = list;
                        return list;
                    }
                }
            } catch {
                /* static file missing or corrupt; fall through to API */
            }
            // Fallback: API call if static file somehow missing
            try {
                const resp = await fetch(
                    getApiUrl("/api/available-underlyings?source=stable"),
                    {
                        headers: {
                            "Content-Type": "application/json",
                            ...(getAuthToken()
                                ? { Authorization: `Bearer ${getAuthToken()}` }
                                : {}),
                        },
                        signal: AbortSignal.timeout(8000),
                    },
                );
                if (resp.ok) {
                    const result = await resp.json();
                    const list = result?.underlyings || [];
                    if (Array.isArray(list) && list.length > 0) {
                        DataService._stableUnderlyingsCache = list;
                        return list;
                    }
                }
            } catch {
                /* ignore */
            }
            return DataService.FALLBACK_UNDERLYINGS;
        }

        // Legacy RTL / other: API call as before
        const timeoutMs = dataSource === "rtl_legacy" ? 30000 : 15000;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const authToken = getAuthToken();
            const fullUrl = getApiUrl(
                `/api/available-underlyings?source=${dataSource}`,
            );
            const response = await fetch(fullUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    ...(authToken
                        ? { Authorization: `Bearer ${authToken}` }
                        : {}),
                },
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                return DataService.FALLBACK_UNDERLYINGS;
            }
            const text = await response.text();
            let result: any = {};
            try {
                result = text && text.trim() ? JSON.parse(text) : {};
            } catch {
                return DataService.FALLBACK_UNDERLYINGS;
            }
            const list = result?.underlyings || [];
            return Array.isArray(list) && list.length > 0
                ? list
                : DataService.FALLBACK_UNDERLYINGS;
        } catch (error) {
            clearTimeout(timeoutId);
            if ((error as Error)?.name === "AbortError") {
                console.warn(
                    "⏱️ [DATA SERVICE] Available underlyings request timed out",
                );
            }
            return DataService.FALLBACK_UNDERLYINGS;
        }
    }

    // Set selected underlying filter
    setSelectedUnderlying(underlying: string): void {
        localStorage.setItem("mmec_selected_underlying", underlying);
        console.log(
            "🔍 [DATA SERVICE] Selected underlying set to:",
            underlying || "ALL",
        );
    }

    // Get current selected underlying
    getSelectedUnderlying(): string {
        return readStablePrimarySessionFirst();
    }
}

export const dataService = new DataService();

// Expose for tracked contracts and other components that need direct data access
if (typeof window !== "undefined")
    (window as any).__mmecDataService = dataService;
